import { useContext } from 'react';
import { ContentMenuContext } from '../create-content-menu-context';
import { useDirectJoinConfluence } from '../use-direct-join-confluence';
import { useEmbeddedPageTracking } from '../use-embedded-page-tracking';
import { ACCESS_REQUEST_CAPABILITIES } from '../utils';

interface CreateContentClickBehavior {
	accessStatus: string;
	createContent: () => void;
	tenantHasConfluence: boolean;
}

const BASE_DELAY = 300;
const MAX_DELAY = 10000;

export const useCreateContentClickBehavior = () => {
	const { embeddedConfluenceSource, setAccessStatus, setIsCrossFlowModalOpen } =
		useContext(ContentMenuContext);
	const fireAnalyticsEvent = useEmbeddedPageTracking();
	const { directJoin } = useDirectJoinConfluence({
		embeddedConfluenceSource,
		onSuccess: () => setAccessStatus(ACCESS_REQUEST_CAPABILITIES.ACCESS_EXISTS),
	});

	const requestAccess = () => {
		fireAnalyticsEvent({
			action: 'requested',
			actionSubject: 'productAccess',
			eventName: 'productAccess requested',
			embeddedConfluenceSource,
			originProduct: 'jira',
			requestedProduct: 'confluence',
			eventType: 'track',
		});
		const tenantUrl = window.location.origin;
		window.open(`${tenantUrl}/wiki`, '_blank');
	};

	const handleDirectJoin = (retries = 15) =>
		directJoin({ retries, baseDelay: BASE_DELAY, maxDelay: MAX_DELAY });
	const openCrossFlowModal = () => {
		setIsCrossFlowModalOpen(true);
	};
	const getCreateContentClickBehavior = ({
		accessStatus,
		createContent,
		tenantHasConfluence,
	}: CreateContentClickBehavior) => {
		if (!tenantHasConfluence) {
			return openCrossFlowModal;
		}

		switch (accessStatus) {
			case ACCESS_REQUEST_CAPABILITIES.REQUEST_ACCESS:
			case ACCESS_REQUEST_CAPABILITIES.PENDING_REQUEST_EXISTS:
			case ACCESS_REQUEST_CAPABILITIES.DENIED_REQUEST_EXISTS:
			case ACCESS_REQUEST_CAPABILITIES.FORBIDDEN:
				return requestAccess;
			case ACCESS_REQUEST_CAPABILITIES.DIRECT_ACCESS:
				return handleDirectJoin;
			case ACCESS_REQUEST_CAPABILITIES.ACCESS_EXISTS:
				return createContent;
			default:
				return requestAccess;
		}
	};
	return getCreateContentClickBehavior;
};
